import type { ReactNode } from "react";

/* Toast store + helpers (no JSX so fast refresh stays happy). UI lives in extras.tsx. */

export type ToastTone = "success" | "error" | "info" | "warning";

export interface ToastRecord {
  id: number;
  tone: ToastTone;
  title: ReactNode;
  description?: ReactNode;
  duration: number;
}

export interface ToastOptions {
  description?: ReactNode;
  /** ms before auto-dismiss (default 4000; 0 keeps it until closed) */
  duration?: number;
}

let toasts: ToastRecord[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

let mountToaster: (() => void) | null = null;
let mounted = false;

/** Called once by extras.tsx so `toast()` can mount the Toaster on demand */
export function registerToasterMount(fn: () => void) {
  mountToaster = fn;
}

export function subscribeToasts(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export const getToasts = () => toasts;

export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function push(tone: ToastTone, title: ReactNode, opts: ToastOptions = {}) {
  if (!mounted && mountToaster && typeof document !== "undefined") {
    mounted = true;
    mountToaster();
  }
  const id = nextId++;
  toasts = [...toasts.slice(-3), { id, tone, title, description: opts.description, duration: opts.duration ?? 4000 }];
  emit();
  return id;
}

/**
 * Fire-and-forget feedback. No provider needed; the toaster mounts itself.
 *   toast.success("Listing saved")
 *   toast.error("Couldn't save", { description: "Check your connection and try again." })
 */
export const toast = Object.assign((title: ReactNode, opts?: ToastOptions) => push("info", title, opts), {
  success: (title: ReactNode, opts?: ToastOptions) => push("success", title, opts),
  error: (title: ReactNode, opts?: ToastOptions) => push("error", title, opts),
  info: (title: ReactNode, opts?: ToastOptions) => push("info", title, opts),
  warning: (title: ReactNode, opts?: ToastOptions) => push("warning", title, opts),
  dismiss: dismissToast,
});

/** "Jane Q Doe" -> "JD" */
export function initialsOf(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] ?? "?").slice(0, 2);
  return letters.toUpperCase();
}
